import { Building2, Mail, Phone, RefreshCw, User } from "lucide-react";
import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "../../contexts/LanguageContext";
import { isPasswordValid } from "../../utils/passwordPolicy";
import {
  AuthAlert,
  AuthButton,
  AuthField,
  AuthHeader,
  AuthInput,
  AuthLink,
  AuthSecondaryButton,
  AuthStatus,
  PasswordInput,
} from "./AuthPrimitives";
import { PasswordRequirements } from "./PasswordRequirements";

// Função utilitária para validação de email
const validateEmail = (email: string): boolean => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

interface RegisterFormProps {
  onSwitchToLogin: () => void;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({
  onSwitchToLogin,
}) => {
  const { register } = useAuth();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    company: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDuplicateEmailError, setShowDuplicateEmailError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFieldErrors((prev) => ({ ...prev, [name]: false }));
    if (error) {
      setError("");
      setShowDuplicateEmailError(false);
    }
  };

  const validateForm = (): boolean => {
    const missing: Record<string, boolean> = {};
    if (!formData.name.trim()) missing.name = true;
    if (!formData.email.trim()) missing.email = true;
    if (!formData.phone.trim()) missing.phone = true;
    if (!formData.password) missing.password = true;
    if (!formData.confirmPassword) missing.confirmPassword = true;

    if (Object.keys(missing).length > 0) {
      setFieldErrors(missing);
      setError(t("Existem campos obrigatórios não preenchidos."));
      return false;
    }

    if (!validateEmail(formData.email.trim())) {
      setFieldErrors({ email: true });
      setError(
        t("O endereço de e-mail informado não possui um formato válido."),
      );
      return false;
    }

    if (formData.name.trim().length < 2) {
      setFieldErrors({ name: true });
      setError(t("Nome deve ter pelo menos 2 caracteres"));
      return false;
    }

    // Mesma regra do backend: telefone com ao menos 10 dígitos (DDD + número)
    if (formData.phone.replace(/\D/g, "").length < 10) {
      setFieldErrors({ phone: true });
      setError(t("Informe um telefone válido, com DDD."));
      return false;
    }

    // Mesma política do backend e do CRM (ver utils/passwordPolicy.ts)
    if (!isPasswordValid(formData.password)) {
      setFieldErrors({ password: true });
      setError(
        t("A senha não atende a todos os requisitos de segurança exigidos."),
      );
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setFieldErrors({ password: true, confirmPassword: true });
      setError(t("As senhas digitadas não coincidem."));
      return false;
    }

    setFieldErrors({});
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setError("");
    setShowDuplicateEmailError(false);

    try {
      await register(
        formData.email,
        formData.name,
        formData.password,
        formData.company || undefined,
        formData.phone || undefined,
      );
      setShowSuccess(true);
    } catch (error) {
      let errorMessage = t("Erro no cadastro");
      let duplicateEmail = false;

      if (error instanceof Error) {
        const message = error.message;

        if (
          message.includes("Usuario com este email ja existe") ||
          message.includes("já existe")
        ) {
          errorMessage = t(
            "Este e-mail já está cadastrado. Tente fazer login ou use outro e-mail.",
          );
          duplicateEmail = true;
        } else if (message.includes("Formato de email invalido")) {
          errorMessage = t("Por favor, insira um e-mail válido.");
        } else if (message.includes("Telefone")) {
          errorMessage = t("Por favor, insira um telefone válido com DDD.");
        } else if (message.includes("obrigatório")) {
          errorMessage = t("Por favor, preencha todos os campos obrigatórios.");
        } else if (message.includes("caracteres")) {
          errorMessage = t("Nome deve ter pelo menos 2 caracteres.");
        } else {
          errorMessage = message;
        }
      }

      setError(errorMessage);
      setShowDuplicateEmailError(duplicateEmail);
      if (duplicateEmail) setFieldErrors({ email: true });
    } finally {
      setIsLoading(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="space-y-6">
        <AuthStatus title={t("Cadastro solicitado!")}>
          <p>
            {t(
              "Seu cadastro foi enviado e aguarda a aprovação de um administrador.",
            )}
          </p>
        </AuthStatus>

        <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-start gap-3">
            <Mail className="mt-0.5 h-5 w-5 shrink-0 text-violet-600" />
            <div className="space-y-1 text-left text-sm">
              <p className="text-slate-600">{t("Cadastro registrado para:")}</p>
              <p className="break-all font-semibold text-slate-900">
                {formData.email}
              </p>
            </div>
          </div>
          <div className="space-y-1 border-t border-slate-100 pt-3 text-left text-xs text-slate-500">
            <p>{t("• O administrador analisará a sua solicitação.")}</p>
            <p>
              {t(
                "• Depois da aprovação, você poderá entrar com este e-mail e a senha cadastrada.",
              )}
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <AuthButton type="button" arrow onClick={onSwitchToLogin}>
            {t("Ir para a tela de login")}
          </AuthButton>
          <AuthSecondaryButton
            type="button"
            onClick={() => setShowSuccess(false)}
          >
            <RefreshCw className="h-4 w-4 text-slate-500" />
            <span>{t("Corrigir dados ou tentar outro e-mail")}</span>
          </AuthSecondaryButton>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <AuthHeader
        title={t("Criar conta corporativa")}
        subtitle={t("Comece a usar e impulsione sua operação.")}
      />

      {error && (
        <AuthAlert
          variant="error"
          title={t("Erro no cadastro")}
          message={error}
        >
          {showDuplicateEmailError && (
            <AuthLink onClick={onSwitchToLogin} className="text-sm underline">
              {t("Ir para página de login")}
            </AuthLink>
          )}
        </AuthAlert>
      )}

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <AuthField label={t("Nome completo")} htmlFor="name" required>
          <AuthInput
            type="text"
            id="name"
            name="name"
            icon={User}
            value={formData.name}
            onChange={handleChange}
            autoComplete="name"
            placeholder={t("Ex: João Silva")}
            error={fieldErrors.name}
            required
          />
        </AuthField>

        <AuthField label={t("Endereço de e-mail")} htmlFor="email" required>
          <AuthInput
            type="email"
            id="email"
            name="email"
            icon={Mail}
            value={formData.email}
            onChange={handleChange}
            autoComplete="email"
            placeholder={t("joao.silva@empresa.com")}
            error={fieldErrors.email}
            required
          />
        </AuthField>

        <AuthField label={t("Telefone")} htmlFor="phone" required>
          <AuthInput
            type="tel"
            id="phone"
            name="phone"
            icon={Phone}
            value={formData.phone}
            onChange={handleChange}
            autoComplete="tel"
            placeholder={t("(11) 91234-5678")}
            error={fieldErrors.phone}
            required
          />
        </AuthField>

        <AuthField label={t("Empresa (opcional)")} htmlFor="company">
          <AuthInput
            type="text"
            id="company"
            name="company"
            icon={Building2}
            value={formData.company}
            onChange={handleChange}
            autoComplete="organization"
            placeholder={t("Nome da sua empresa")}
          />
        </AuthField>

        <AuthField label={t("Senha de acesso")} htmlFor="password" required>
          <PasswordInput
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            autoComplete="new-password"
            placeholder={t("Mínimo 8 caracteres, com letras, número e símbolo")}
            error={fieldErrors.password}
            required
          />
        </AuthField>

        <AuthField
          label={t("Confirmar senha")}
          htmlFor="confirmPassword"
          required
        >
          <PasswordInput
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            autoComplete="new-password"
            placeholder={t("Digite a mesma senha novamente")}
            error={fieldErrors.confirmPassword}
            required
          />
        </AuthField>

        <PasswordRequirements
          password={formData.password}
          confirmPassword={formData.confirmPassword}
        />

        <p className="text-xs text-slate-500">
          {t("Ao criar sua conta corporativa, você concorda com nossos")}{" "}
          <a
            href="https://virtusautomation.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-purple-700 transition-colors hover:text-[#6d28d9] hover:underline"
          >
            {t("Termos de Uso")}
          </a>{" "}
          {t("e")}{" "}
          <a
            href="https://virtusautomation.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-purple-700 transition-colors hover:text-[#6d28d9] hover:underline"
          >
            {t("Política de Privacidade (LGPD)")}
          </a>
          .
        </p>

        <AuthButton
          type="submit"
          arrow
          loading={isLoading}
          loadingText={t("Criando sua conta corporativa...")}
        >
          {t("Finalizar cadastro")}
        </AuthButton>
      </form>

      <p className="text-center text-sm text-slate-600">
        {t("Já possui uma conta corporativa?")}{" "}
        <AuthLink onClick={onSwitchToLogin}>{t("Fazer login")}</AuthLink>
      </p>
    </div>
  );
};
